import { HTMLAttributesSignalExcluded } from '../utils/UNSAFE_attributeUtils';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';

import { assignInlineVars } from '@vanilla-extract/dynamic';
import { classNames } from '../utils/UNSAFE_classNames';
import { windowOverlayStyles } from './themes/WindowOverlayStyles.css';
import { windowOverlayVars } from './themes/WindowOverlayContract.css';
import { useUser } from '../hooks/UNSAFE_useUser';
import { Offset } from '../UNSAFE_Floating';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { WindowOverlayRedwoodTheme } from './themes/redwood/WindowOverlayTheme';

export { Offset };

type IntrinsicProps = Pick<HTMLAttributesSignalExcluded<HTMLDivElement>, 'children'>;

export type WindowOverlayPlacement =
  | 'center'
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'start'
  | 'start-top'
  | 'start-bottom'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'end'
  | 'end-top'
  | 'end-bottom';

const offsetStyle = (offset?: Offset) => {
  return typeof offset === 'number'
    ? assignInlineVars({
        [windowOverlayVars.notificationMainAxisOffset]: `${offset}px`,
        [windowOverlayVars.notificationCrossAxisOffset]: `0px`
      })
    : assignInlineVars({
        [windowOverlayVars.notificationMainAxisOffset]: `${offset?.mainAxis ?? 0}px`,
        [windowOverlayVars.notificationCrossAxisOffset]: `${offset?.crossAxis ?? 0}px`
      });
};

type WindowOverlayProps = IntrinsicProps & {
  /**
   * Offset displaces the child element of the WindowOverlay component from its core placement along the specified axes.
   *
   * Offset could be a number or an object
   *
   * An object has the following type:
   * <pre>
   *   {
   *     mainAxis?: number;
   *     crossAxis?: number;
   *   }
   * </pre>
   * <p><code>mainAxis</code> - represents the distance (gutter or margin) between the child element and the viewport along the mainAxis.</p>
   * <p><code>crossAxis</code> - represents the distance (gutter or margin) between the child element and the viewport along the crossAxis.</p>
   *
   * where,
   * <p>The <code>mainAxis</code> is an axis that runs along the side of the placement, i.e. the part before the hyphen (top-*, bottom-*, start-*, end-*).</p>
   * <p>The <code>crossAxis</code> is an axis that runs along the alignment of the placement, i.e. the part after the hyphen (*-start, *-end, *-top, *-bottom).</p>
   *
   * For the placement value 'center' the convention for the offset type is that mainAxis defines offset from the top, crossAxis defines offset from the left side of the view port.
   *
   * A number is a shortcut for setting the mainAxis.
   */
  offset?: Offset;
  /**
   * Placement of the WindowOverlay element relative to the viewport.
   * Possible values are:
   * <p> <code>'center' </code> <code>'top' </code> <code>'top-start' </code> <code> 'top-end' </code>
   * <code> 'end' </code> <code> 'end-top'  </code> <code> 'end-bottom' </code>
   * <code> 'bottom'  </code> <code> 'bottom-start'  </code> <code> 'bottom-end' </code>
   * <code> 'start' </code> <code> 'start-top'  </code> <code> 'start-bottom' </code>
   * </p>
   */
  placement?: WindowOverlayPlacement;
};

/**
 * A WindowOverlay component allows the user to place a child component relative to the viewport
 */
export const WindowOverlay = ({ children, ...props }: WindowOverlayProps) => {
  const { direction } = useUser();
  const isLtr = direction === 'ltr';

  if (props.placement == null) {
    props.placement = 'center';
  }

  const { styles, baseTheme: windowOverlayTheme } = useComponentTheme(WindowOverlayRedwoodTheme);
  const { baseStyle, gridStyles, placementStyles } = styles;

  const placementInterpolations = [
    ({ placement = 'center' }: Pick<WindowOverlayProps, 'placement'>) => ({
      class: placementStyles[placement]
    })
  ];

  const interpolations = [...Object.values(placementInterpolations)];

  const styleInterpolations = mergeInterpolations<WindowOverlayProps>(interpolations);

  const { class: cls, ...allStyles } = styleInterpolations(props);

  const offsetClasses = (position?: string, isLtr?: boolean, theme?: string) => {
    if (position == null) {
      return;
    }
    let logicalPosition;
    switch (position) {
      case 'top-start':
      case 'center':
        logicalPosition = isLtr
          ? windowOverlayStyles['top-left']
          : windowOverlayStyles['top-right'];
        break;
      case 'top-end':
        logicalPosition = isLtr
          ? windowOverlayStyles['top-right']
          : windowOverlayStyles['top-left'];
        break;
      case 'bottom-start':
        logicalPosition = isLtr
          ? windowOverlayStyles['bottom-left']
          : windowOverlayStyles['bottom-right'];
        break;
      case 'bottom-end':
        logicalPosition = isLtr
          ? windowOverlayStyles['bottom-right']
          : windowOverlayStyles['bottom-left'];
        break;
      case 'start-top':
        logicalPosition = isLtr
          ? windowOverlayStyles['left-top']
          : windowOverlayStyles['right-top'];
        break;
      case 'end-top':
        logicalPosition = isLtr
          ? windowOverlayStyles['right-top']
          : windowOverlayStyles['left-top'];
        break;
      case 'start-bottom':
        logicalPosition = isLtr
          ? windowOverlayStyles['left-bottom']
          : windowOverlayStyles['right-bottom'];
        break;
      case 'end-bottom':
        logicalPosition = isLtr
          ? windowOverlayStyles['right-bottom']
          : windowOverlayStyles['left-bottom'];
        break;
      case 'start':
        logicalPosition = isLtr ? windowOverlayStyles['left'] : windowOverlayStyles['right'];
        break;
      case 'end':
        logicalPosition = isLtr ? windowOverlayStyles['right'] : windowOverlayStyles['left'];
        break;
      case 'top':
        logicalPosition = windowOverlayStyles['top'];
        break;
      case 'bottom':
        logicalPosition = windowOverlayStyles['bottom'];
        break;
      default:
        break;
    }
    return classNames([windowOverlayStyles.notificationPosition, logicalPosition, theme]);
  };
  const classes = offsetClasses(props.placement, isLtr, windowOverlayTheme);

  const windowOverlayOffsetStyle = offsetStyle(props.offset);

  return (
    <div class={classNames([baseStyle, gridStyles, cls])} style={allStyles}>
      <div
        class={classes}
        style={windowOverlayOffsetStyle !== undefined ? windowOverlayOffsetStyle : undefined}>
        {children}
      </div>
    </div>
  );
};
